/**
 * Responsibility: pick the right rendition of a catalogue card image.
 *
 * The bucket serves a fixed set of widths - 60, 120, 240, 320 and 1600 -
 * and the URL carries the width in its last segment. Measured: 400, 640,
 * 800 and 1000 all 404, so a width has to be snapped to one that exists.
 *
 * Holdings were stored with the 240, which is only 1.6x a 150px tile and
 * reads as soft on any hidpi screen - the grid looked like it was showing
 * previews because it effectively was. The 320 is crisp at two-times and
 * costs twenty kilobytes.
 *
 * Rewritten on the client rather than migrated, so rows written before this
 * get the better rendition too without a schema change.
 */

/** The widths the bucket actually has. */
const AVAILABLE = [60, 120, 240, 320, 1600] as const;

/**
 * Crisp at two-times on a grid tile.
 *
 * The tile is 208 wide, so two-times wants 416 and the bucket's nearest
 * real rendition above that is 1600. 320 stays: it is 1.5x, which is
 * sharp enough on the artwork, where 1600 is a megabyte a card and a
 * shelf of forty would be forty megabytes to look at a wall of thumbnails.
 */
export const GRID_WIDTH = 320;

/** The full-size rendition, for a dialog or a lightbox. */
export const FULL_WIDTH = 1600;

const SIZED = /\/(\d+)\.jpg$/;

/**
 * The same image at a different width.
 *
 * Returns the original unchanged when it is not one of the bucket's URLs -
 * BetterPop card renders come from elsewhere and have no size in the path.
 */
export function atWidth(url: string | null | undefined, width: number): string | null {
  if (!url) return null;
  if (!SIZED.test(url)) return url;

  const nearest = AVAILABLE.reduce((best, candidate) =>
    Math.abs(candidate - width) < Math.abs(best - width) ? candidate : best,
  );
  return url.replace(SIZED, `/${nearest}.jpg`);
}
